import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  TrendingUp,
  Co2Outlined,
  PrecisionManufacturing,
  Build,
  Storage,
  Verified,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BRAND, BRAND_SERVICES, BRAND_MOSAIC } from '../config/brand';
import { BrandHero } from '../components/brand/BrandHero';
import { ImageMosaic } from '../components/brand/ImageMosaic';

const companyHighlights = [
  {
    title: 'Believe When You See',
    description: 'Tangible quality you can witness — from our facility floor to your finished product.',
    icon: <Verified sx={{ fontSize: 40, color: BRAND.colors.navy }} />,
  },
  {
    title: 'African Excellence',
    description: 'Proudly manufacturing textiles, furniture, metalworks, and footwear for global markets.',
    icon: <TrendingUp sx={{ fontSize: 40, color: BRAND.colors.navyLight }} />,
  },
  {
    title: 'Sustainable Craft',
    description: 'Responsible production practices that respect people, materials, and the environment.',
    icon: <Co2Outlined sx={{ fontSize: 40, color: '#2e7d5a' }} />,
  },
];

export const Home: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: BRAND.colors.creamLight, minHeight: '100vh' }}>
      <BrandHero
        title={`${BRAND.name} ${BRAND.subtitle}`}
        subtitle={BRAND.tagline}
        description="Premium textiles, smart furniture, precision metalworks, and quality footwear — manufactured with pride in Africa."
        backgroundImage={BRAND.images.facilityExterior}
        primaryAction={{ label: 'Explore Products', onClick: () => navigate('/products') }}
        secondaryAction={{ label: 'Book a Facility Tour', onClick: () => navigate('/book-tour') }}
      />

      {/* Brand story strip */}
      <Box sx={{ py: 6, bgcolor: BRAND.colors.navy, color: BRAND.colors.cream }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src={BRAND.images.logo}
                alt="Monio Cluster logo"
                sx={{
                  width: '100%',
                  maxWidth: 380,
                  borderRadius: 3,
                  boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
                }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: BRAND.colors.white }}>
                Manufacturers You Can Trust
              </Typography>
              <Typography sx={{ opacity: 0.92, lineHeight: 1.8, fontSize: '1.05rem' }}>
                Monio Cluster Manufacturers brings together skilled teams, modern facilities, and
                a commitment to delivering products that speak for themselves. From our gate to your
                doorstep — believe when you see.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <ImageMosaic
        title="Inside Monio Cluster"
        subtitle="Our people, our facility, and the products we build every day"
        items={BRAND_MOSAIC.map((item) => ({
          ...item,
          caption: item.alt.replace('Monio Cluster ', ''),
        }))}
      />

      {/* Highlights */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" align="center" sx={{ fontWeight: 800, color: BRAND.colors.navy, mb: 6 }}>
          Why Choose Monio Cluster
        </Typography>
        <Grid container spacing={3}>
          {companyHighlights.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Card
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  border: `1px solid ${BRAND.colors.cream}`,
                  bgcolor: BRAND.colors.white,
                }}
              >
                <Box sx={{ mb: 2 }}>{item.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.colors.navy, mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography color="text.secondary">{item.description}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services / products mosaic */}
      <Box sx={{ py: 10, bgcolor: BRAND.colors.white }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" sx={{ fontWeight: 800, color: BRAND.colors.navy, mb: 1 }}>
            What We Manufacture
          </Typography>
          <Typography align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}>
            Textiles, furniture, metalworks, and footwear — each division backed by dedicated teams and real production capacity.
          </Typography>
          <Grid container spacing={3}>
            {BRAND_SERVICES.map((service) => (
              <Grid item xs={12} sm={6} md={3} key={service.title}>
                <Card
                  sx={{
                    height: '100%',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: `2px solid ${BRAND.colors.cream}`,
                    '&:hover': { borderColor: BRAND.colors.navyLight },
                  }}
                  onClick={() => navigate('/services')}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={service.image}
                    alt={service.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="overline" sx={{ color: BRAND.colors.navyLight, fontWeight: 700 }}>
                      {service.category}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.colors.navy }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team + facility split */}
      <Box sx={{ py: 10, bgcolor: BRAND.colors.cream }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={BRAND.images.teamSign}
                alt="Monio Cluster team"
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  boxShadow: '0 20px 50px rgba(27,77,137,0.2)',
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: BRAND.colors.navy, mb: 2 }}>
                Our People Make the Difference
              </Typography>
              <Typography color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                Behind every product is a team of dedicated professionals — from production floor
                to quality control — united by the Monio Cluster standard of excellence.
              </Typography>
              <List dense>
                {['Skilled production teams', 'On-site quality assurance', 'Modern manufacturing facility', 'Client-focused partnerships'].map((text) => (
                  <ListItem key={text} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: BRAND.colors.navy }} />
                    </ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="contained"
                sx={{ mt: 2, bgcolor: BRAND.colors.navy, '&:hover': { bgcolor: BRAND.colors.navyDark } }}
                onClick={() => navigate('/about/team')}
              >
                Meet Our Team
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Manufacturing excellence */}
      <Box sx={{ py: 10, bgcolor: BRAND.colors.navyDark, color: BRAND.colors.cream }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: BRAND.colors.white, mb: 2 }}>
                Manufacturing Excellence
              </Typography>
              <Typography sx={{ opacity: 0.9, mb: 3, lineHeight: 1.8 }}>
                Our facility combines traditional craftsmanship with modern production systems —
                delivering consistent quality across textiles, furniture, metalworks, and footwear.
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Build sx={{ color: BRAND.colors.cream }} />
                  <Typography variant="body2">Custom fabrication</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PrecisionManufacturing sx={{ color: BRAND.colors.cream }} />
                  <Typography variant="body2">Precision engineering</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Storage sx={{ color: BRAND.colors.cream }} />
                  <Typography variant="body2">Supply chain ready</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={BRAND.images.gateSign}
                alt="Monio Cluster manufacturing gate"
                sx={{ width: '100%', borderRadius: 3, border: `4px solid ${BRAND.colors.cream}` }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${BRAND.colors.navy} 0%, ${BRAND.colors.navyLight} 100%)`,
            color: BRAND.colors.white,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
            Ready to Partner with Monio Cluster?
          </Typography>
          <Typography sx={{ opacity: 0.9, mb: 4, maxWidth: 560, mx: 'auto' }}>
            Request a quote, schedule a facility tour, or speak with our team today.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{ bgcolor: BRAND.colors.cream, color: BRAND.colors.navy, fontWeight: 700 }}
              onClick={() => navigate('/quote')}
            >
              Get a Quote
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ borderColor: BRAND.colors.cream, color: BRAND.colors.cream }}
              onClick={() => navigate('/contact')}
            >
              Contact Us
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};
